import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { authWithEmail, clearSession, fetchRoster, restoreSession } from "../lib/api";
import { tryEmailTokenLogin } from "../lib/emailTokenLogin";
import { supabase } from "../lib/supabase";
import type { Attendee } from "../lib/attendees";
import { resetDeck } from "../lib/deck";
import {
  captureAttendeeSignedIn,
  identifyPickleAttendee,
  resetPostHogUser,
} from "../posthog";

type AttendeeContextValue = {
  me: Attendee | null;
  attendees: readonly Attendee[];
  bootstrapping: boolean;
  /** Supabase session email when signed in via magic link (even if not on guest list). */
  sessionEmail: string | null;
  logout: () => void;
};

const AttendeeContext = createContext<AttendeeContextValue | null>(null);

async function bridgeSupabaseSession(session: Session | null): Promise<{
  me: Attendee;
  roster: readonly Attendee[];
} | null> {
  const email = session?.user?.email?.trim().toLowerCase();
  if (!email) return null;

  const auth = await authWithEmail(email);
  const roster = await fetchRoster(auth.token);
  return { me: auth.me, roster };
}

export const AttendeeProvider = ({ children }: { children: ReactNode }) => {
  const [me, setMe] = useState<Attendee | null>(null);
  const [attendees, setAttendees] = useState<readonly Attendee[]>([]);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);

  const applyRosterSession = useCallback(
    (nextMe: Attendee, roster: readonly Attendee[]) => {
      identifyPickleAttendee(nextMe);
      captureAttendeeSignedIn();
      setMe(nextMe);
      setAttendees(roster);
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;

    async function hydrateFromSupabase(session: Session | null) {
      const email = session?.user?.email?.trim().toLowerCase() ?? null;
      if (!cancelled) setSessionEmail(email);
      if (!email) return false;

      try {
        const bridged = await bridgeSupabaseSession(session);
        if (cancelled || !bridged) return false;
        applyRosterSession(bridged.me, bridged.roster);
        return true;
      } catch {
        return false;
      }
    }

    async function init() {
      if (new URLSearchParams(window.location.search).has("token")) {
        await tryEmailTokenLogin();
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (await hydrateFromSupabase(session)) {
        if (!cancelled) setBootstrapping(false);
        return;
      }

      const restored = await restoreSession();
      if (cancelled) return;
      if (restored) {
        applyRosterSession(restored.me, restored.roster);
      }
      setBootstrapping(false);
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (cancelled) return;
      const email = session?.user?.email?.trim().toLowerCase() ?? null;
      setSessionEmail(email);

      if (
        (event === "SIGNED_IN" || event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") &&
        email
      ) {
        const bridged = await hydrateFromSupabase(session);
        if (bridged) setBootstrapping(false);
      }

      if (event === "SIGNED_OUT") {
        setSessionEmail(null);
        setMe(null);
        setAttendees([]);
      }
    });

    void init();

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [applyRosterSession]);

  const logout = useCallback(() => {
    resetPostHogUser();
    resetDeck();
    clearSession();
    void supabase.auth.signOut();
    setSessionEmail(null);
    setMe(null);
    setAttendees([]);
  }, []);

  const value = useMemo(
    () => ({
      me,
      attendees,
      bootstrapping,
      sessionEmail,
      logout,
    }),
    [me, attendees, bootstrapping, sessionEmail, logout],
  );

  return (
    <AttendeeContext.Provider value={value}>{children}</AttendeeContext.Provider>
  );
};

export const useAttendees = (): AttendeeContextValue => {
  const ctx = useContext(AttendeeContext);
  if (!ctx) {
    throw new Error("useAttendees must be used within AttendeeProvider");
  }
  return ctx;
};
